import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'


// Initial state
const initialState: WorkspaceState = {
    organizations: [],
    currentOrganization: null,
    userPermissions: [],
    organizationData: null,
    loading: false,
    error: null
}

// Async thunk to fetch organizations
export const fetchOrganizations = createAsyncThunk(
    'workspace/fetchOrganizations',
    async (_, { rejectWithValue }) => {
        try {
            const response = await fetch('/user/organizations', {
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch organizations')
            }

            const data = await response.json()
            return data.data || []
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch organizations')
        }
    }
)

// Async thunk to fetch organization details
export const fetchOrganizationData = createAsyncThunk(
    'workspace/fetchOrganizationData',
    async (organizationId: string | number, { rejectWithValue }) => {
        try {
            const response = await fetch(`/organization/${organizationId}/get-organization`, {
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch organization data')
            }

            const data = await response.json()
            return data.data?.[0] || null
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch organization data')
        }
    }
)

// Async thunk to fetch user permissions
export const fetchUserPermissions = createAsyncThunk(
    'workspace/fetchUserPermissions',
    async ({ organizationId, userId }: { organizationId: string | number, userId: number }, { rejectWithValue }) => {
        try {
            const response = await fetch(`/organization/${organizationId}/user/${userId}/permissions`, {
                credentials: 'include',
                headers: {
                    'Accept': 'application/json',
                    'X-Requested-With': 'XMLHttpRequest'
                }
            })

            if (!response.ok) {
                throw new Error('Failed to fetch user permissions')
            }

            const data = await response.json()
            return data.data || []
        } catch (error) {
            return rejectWithValue(error instanceof Error ? error.message : 'Failed to fetch user permissions')
        }
    }
)

// Create the workspace slice
const workspaceSlice = createSlice({
    name: 'workspace',
    initialState,
    reducers: {
        // Synchronous actions
        setCurrentOrganization: (state, action: PayloadAction<string | number | null>) => {
            state.currentOrganization = action.payload
            state.error = null
        },
        setOrganizations: (state, action: PayloadAction<OrganizationRow[]>) => {
            state.organizations = action.payload
            state.error = null
        },
        setOrganizationData: (state, action: PayloadAction<OrganizationDetails | null>) => {
            state.organizationData = action.payload
            state.error = null
        },
        setUserPermissions: (state, action: PayloadAction<string[]>) => {
            state.userPermissions = action.payload
            state.error = null
        },
        clearWorkspace: (state) => {
            state.organizations = []
            state.currentOrganization = null
            state.userPermissions = []
            state.organizationData = null
            state.loading = false
            state.error = null
        },
        setError: (state, action: PayloadAction<string | null>) => {
            state.error = action.payload
        },
        clearError: (state) => {
            state.error = null
        }
    },
    extraReducers: (builder) => {
        // Handle fetchOrganizations async thunk
        builder
            .addCase(fetchOrganizations.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchOrganizations.fulfilled, (state, action) => {
                state.loading = false
                state.organizations = action.payload
                state.error = null
            })
            .addCase(fetchOrganizations.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload as string || 'Failed to fetch organizations'
            })

        // Handle fetchOrganizationData async thunk
        builder
            .addCase(fetchOrganizationData.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchOrganizationData.fulfilled, (state, action) => {
                state.loading = false
                state.organizationData = action.payload
                state.error = null
            })
            .addCase(fetchOrganizationData.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload as string || 'Failed to fetch organization data'
            })

        // Handle fetchUserPermissions async thunk
        builder
            .addCase(fetchUserPermissions.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchUserPermissions.fulfilled, (state, action) => {
                state.loading = false
                state.userPermissions = action.payload
                state.error = null
            })
            .addCase(fetchUserPermissions.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload as string || 'Failed to fetch user permissions'
            })
    }
})

// Export actions
export const {
    setCurrentOrganization,
    setOrganizations,
    setOrganizationData,
    setUserPermissions,
    clearWorkspace,
    setError,
    clearError
} = workspaceSlice.actions

// Export the reducer
export default workspaceSlice.reducer